import {
  ClientPrintJob,
  DefaultPrinter,
  FileSourceType,
  InstalledPrinter,
  JSPrintManager,
  PrintFilePDF,
  WSStatus
} from "jsprintmanager";
import mainConfig from "../common/config/main.config";
import { PrintOptions, PrintServerOptions, ServerConfig } from "./types";
import PrintSettingsCache from "../components/printSettings/cache/PrintSettingsCache";
import { PrintServerVersion } from "../common/enums";
import PrintServiceV7 from "./PrintServiceV7";
import GlobalErrorHandler from "../common/utils/errorHandlers/GlobalErrorHandler";

const errorHandler = GlobalErrorHandler.getInstance();

//Check JSPM WebSocket status
class PrintService {
  private static _selectedPrinterVersion;

  static jspmWSStatus() {
    try {
      if (JSPrintManager.websocket_status === WSStatus.Open) return true;
      else if (JSPrintManager.websocket_status === WSStatus.Closed) {
        console.log(
          "JSPrintManager (JSPM) is not installed or not running! Download JSPM Client App from https://neodynamic.com/downloads/jspm"
        );
        return false;
      } else if (JSPrintManager.websocket_status === WSStatus.Blocked) {
        console.log("JSPM has blocked this website!");
        return false;
      }
      return false;
    } catch (e) {
      return false;
    }
  }

  static async isPrintServerConnected() {
    const version = await this.getPrintServerVersion(this.getServerConfig({ useCache: true }).ip);
    if (version === PrintServerVersion.V7) {
      return PrintServiceV7.isPrintServerConnected();
    }
    return JSPrintManager.websocket_status === WSStatus.Open;
  }

  static async getPrintServerVersion(ip?: string) {
    if (this._selectedPrinterVersion) {
      return this._selectedPrinterVersion;
    }
    
    const isV7Available = await this.is_jspm_installed(ip, 27508);
    
    if (isV7Available) {
      console.log("Print server: V7 is used.");
      this._selectedPrinterVersion = PrintServerVersion.V7;
      return PrintServerVersion.V7;
    } else {
      console.log("Print server: V4 is used.");
      this._selectedPrinterVersion = PrintServerVersion.V4;
      return PrintServerVersion.V4;
    }
  }

  static is_jspm_installed(ip, appPort) {
    return new Promise((resolve) => {
      const ws = new WebSocket(`wss://${ip || "localhost"}:${appPort}`);
      let isResolved = false;

      const resolveOnce = (value: boolean) => {
        if (!isResolved) {
          isResolved = true;
          resolve(value);
        }
      };

      ws.onmessage = (mj) => {
        try {
          console.log(mj.data);
          const m = JSON.parse(mj.data);
          if ("version" in m && "connection" in m && m["connection"] === "CONNECTED") {
            resolveOnce(true);
            return;
          }
        } catch (e) {
          console.error('Error parsing JSPrintManager message:', e);
          resolveOnce(false);
        } finally {
          ws.close();
        }
      };

      ws.onerror = (event) => {
        console.log("JSPrintManager WebSocket not available:", event.type);
        resolveOnce(false);
        ws.close();
      };

      ws.onclose = (event) => {
        if (event.code !== 1000) { // 1000 is normal closure
          console.log(`JSPrintManager WebSocket closed: code ${event.code}`);
        }
        resolveOnce(false);
      };

      // Add timeout to prevent hanging connections
      setTimeout(() => {
        if (ws.readyState === WebSocket.CONNECTING) {
          console.log("JSPrintManager WebSocket connection timeout");
          ws.close();
          resolveOnce(false);
        }
      }, 5000); // 5 second timeout
    });
  }

  static getPrinter(printerSelected = "") {
    const cpj = new ClientPrintJob();
    if (printerSelected) {
      cpj.clientPrinter = new InstalledPrinter(printerSelected);
    } else {
      cpj.clientPrinter = new DefaultPrinter();
    }
    return cpj;
  }

  static async print(blob: any, options: PrintOptions = {}) {
    const { ip } = this.getServerConfig({ useCache: true });
    const version = await this.getPrintServerVersion(ip);

    if (version === PrintServerVersion.V7) {
      return PrintServiceV7.print(blob, options);
    }

    try {
      if (this.jspmWSStatus()) {
        const cpj = this.getPrinter(options.selectedPrinter);
        const myPdfFile = new PrintFilePDF(blob, FileSourceType.BLOB, "myFileToPrint.pdf", 1);
        cpj.files.push(myPdfFile);
        cpj.sendToClient();
        return;
      }
      throw new Error(`Print server not connected. WebSocket status: ${JSPrintManager.websocket_status}`);
    } catch (e) {
      this.stop();
      const error = e instanceof Error ? e : new Error(`Print operation failed: ${e}`);
      errorHandler.handlePrintServerError(error, 'print_v4');
      throw error;
    }
  }

  static async printZPL(zplStr: string, options: PrintOptions) {
    const version = await this.getPrintServerVersion(this.getServerConfig({ useCache: true }).ip);

    if (version === PrintServerVersion.V7) {
      return PrintServiceV7.printZPL(zplStr, options);
    }

    try {
      if (this.jspmWSStatus()) {
        const cpj = this.getPrinter(options.selectedPrinter);
        cpj.printerCommands = zplStr;
        cpj.sendToClient();
        return;
      }
      throw new Error(`Print server not connected for ZPL printing. WebSocket status: ${JSPrintManager.websocket_status}`);
    } catch (e) {
      this.stop();
      const error = e instanceof Error ? e : new Error(`ZPL print operation failed: ${e}`);
      errorHandler.handlePrintServerError(error, 'printZPL_v4');
      throw error;
    }
  }

  static getCachedConnectionValues() {
    const cached = PrintSettingsCache.get().settingsValues;
    return { serverIP: cached?.serverIP, serverPort: +cached?.serverPort || undefined };
  };

  static getServerConfig = (options?: Partial<PrintServerOptions>) => {
    let serverIP: ServerConfig = undefined;
    let serverPort: ServerConfig = undefined;

    if (options?.useCache) {
      const { serverIP: ip, serverPort: port } = this.getCachedConnectionValues();
      if (ip) {
        serverIP = ip;
      }
      if (port) {
        serverPort = port;
      }
    } else if (options?.ip) {
      serverIP = options.ip;
      serverPort = options.port;
    }
    return { ip: serverIP, port: serverPort };
  };

  static async setupPdfWS(options?: Partial<PrintServerOptions>) {
    const { ip } = this.getServerConfig(options);

    const version = await this.getPrintServerVersion(ip);

    if (version === PrintServerVersion.V7) {
      return PrintServiceV7.setupPdfWS(options);
    }

    try {
      await this.stop();

      const baseApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT)?.registrationApiUrl;
      JSPrintManager.license_url = `${baseApiUrl}/v1/PrintServer/license`;
      JSPrintManager.auto_reconnect = true;
      
      // Wrap JSPrintManager.start() to catch any Event objects it might reject with
      try {
        await JSPrintManager.start(true, ip, 24443);
      } catch (startError) {
        // Convert any Event objects or other non-Error rejections to proper Error
        const error = startError instanceof Error 
          ? startError 
          : new Error(`JSPrintManager.start failed: ${(startError as any)?.type || 'unknown'}`);
        throw error;
      }
      
      return Promise.resolve();
    } catch (e) {
      PrintService.stop();
      const error = e instanceof Error ? e : new Error("Unable to connect to Print Server.");
      return Promise.reject(error);
    }
  };

  static async stop() {
    const { ip } = this.getServerConfig({ useCache: true });
    const version = await this.getPrintServerVersion(ip);

    if (version === PrintServerVersion.V7) {
      return PrintServiceV7.stop();
    }

    try {
      if (JSPrintManager.websocket_status !== WSStatus.Closed) {
        JSPrintManager.stop();
      }
    } catch (e) {
    }
  };

  static async getAvailablePrinters() {
    const version = await this.getPrintServerVersion(this.getServerConfig({ useCache: true }).ip);

    if (version === PrintServerVersion.V7) {
      return PrintServiceV7.getAvailablePrinters();
    }

    try {
      return await JSPrintManager.getPrinters();
    } catch (e) {
      return [];
    }
  };
}

export default PrintService;
